/**
 * User Profile Context
 *
 * Provides shared user profile state across the application.
 * When profile is updated, all consumers see the change immediately.
 */

import { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo, type ReactNode } from 'react'
import { authenticatedRequest, ApiError } from '../api/client'
import { useAuthContext } from './AuthContext'
import type { UserMeResponse, UserProfile } from '../services/auth'
import { CURRENT_AGREEMENT_VERSION, CURRENT_EXTERNAL_AGENT_TERMS_VERSION } from '@/features/agreement/constants'
import { SMS_CONSENT_COPY_VERSION, type SmsConsentRequest } from '@/features/settings/components/ProfileForm/smsConsent'

/**
 * Input for updating user profile
 */
export interface UserProfileUpdateInput {
  first_name: string
  last_name: string
  public_email?: string | null
  public_phone?: string | null
  company_name?: string | null
  markets?: string[] | null
  // Qualification fields (optional - only sent for unanswered questions)
  wholesale_intent?: string | null
  proof_of_funds_available?: string | null
  proof_of_funds_file_key?: string | null
  proof_of_funds_file_name?: string | null
  hard_money_lender?: string | null
  hard_money_lender_explanation?: string | null
  annual_purchase_volume?: string | null
  // Agent license fields — set via the external-agent prefill step (MRG-281)
  license_number?: string | null
  license_state?: string | null
}

/**
 * User profile context value
 */
export interface UserProfileContextValue {
  /** User data from /users/me API */
  data: UserMeResponse | null
  /** Loading state */
  isLoading: boolean
  /** Error if fetch failed */
  error: string | null
  /** Refetch function to manually trigger a new request */
  refetch: () => void
  /** Update profile function */
  updateProfile: (input: UserProfileUpdateInput) => Promise<UserProfile>
  /** Complete onboarding function */
  completeOnboarding: () => Promise<UserProfile>
  /** Complete product tour function */
  completeProductTour: () => Promise<UserProfile>
  /** Reset product tour for replay */
  resetProductTour: () => Promise<UserProfile>
  /** Sign the investor agreement */
  signAgreement: (fullLegalName: string, nameMismatchConfirmed?: boolean) => Promise<UserProfile>
  /** Record an SMS opt-in or opt-out against the profile phone (MRG-1140) */
  recordSmsConsent: (consented: boolean) => Promise<UserProfile>
  /** Accept the external-agent ToS (MRG-281). External agents only. */
  acceptExternalAgentTerms: (fullLegalName: string, nameMismatchConfirmed?: boolean) => Promise<UserProfile>
  /** Mark a single training module complete or incomplete (MRG-281). */
  updateTrainingProgress: (moduleId: string, completed: boolean) => Promise<UserProfile>
  /** Mark all required training as complete (MRG-281). External agents only. */
  completeTraining: () => Promise<UserProfile>
  /** Reset training so the agent can replay (MRG-281). External agents only. */
  resetTraining: () => Promise<UserProfile>
  /** Whether the user needs to complete onboarding */
  needsOnboarding: boolean
  /** Whether the user needs to see the product tour */
  needsProductTour: boolean
  /** Whether the investor needs to sign the new agreement */
  needsNewAgreement: boolean
  /** Whether an external agent still needs to accept the current ToS version */
  needsExternalAgentTerms: boolean
  /** Whether an external agent still needs to complete training */
  needsTraining: boolean
}

const UserProfileContext = createContext<UserProfileContextValue | null>(null)

interface UserProfileProviderProps {
  children: ReactNode
}

/**
 * User Profile Provider
 *
 * Wraps the app to provide shared user profile state.
 */
export function UserProfileProvider({ children }: UserProfileProviderProps) {
  const { token, logout, isAuthenticated, roles } = useAuthContext()
  const [data, setData] = useState<UserMeResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refetchTrigger, setRefetchTrigger] = useState(0)
  const logoutRef = useRef(logout)
  const tokenRef = useRef(token)

  // Keep refs up to date
  useEffect(() => {
    logoutRef.current = logout
    tokenRef.current = token
  }, [logout, token])

  useEffect(() => {
    // Don't fetch if not authenticated or no token
    // Use tokenRef to avoid refetching on every token refresh — the profile
    // data doesn't change when the access token is refreshed, and refetching
    // sets isLoading=true which unmounts the entire router tree.
    if (!isAuthenticated || !tokenRef.current) {
      setIsLoading(false)
      setError(null)
      setData(null)
      return
    }

    const fetchUserProfile = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const profile = await authenticatedRequest<UserMeResponse>('/users/me', tokenRef.current!)
        setData(profile)
        setError(null)
      } catch (err) {
        if (err instanceof ApiError) {
          // If 401, trigger logout - session expired
          if (err.status === 401) {
            console.error('Session expired, logging out')
            logoutRef.current()
            setError('Session expired. Please log in again.')
          } else if (err.status === 403) {
            setError('Access denied')
          } else if (err.status && err.status >= 500) {
            setError('Service temporarily unavailable')
          } else {
            setError(err.message || 'Failed to load user profile')
          }
        } else {
          setError('An unexpected error occurred')
          console.error('Unexpected error fetching user profile:', err)
        }
        setData(null)
      } finally {
        setIsLoading(false)
      }
    }

    fetchUserProfile()
  }, [isAuthenticated, refetchTrigger])

  const refetch = useCallback(() => {
    setRefetchTrigger((prev) => prev + 1)
  }, [])

  /**
   * Update user profile
   */
  const updateProfile = useCallback(async (input: UserProfileUpdateInput): Promise<UserProfile> => {
    if (!tokenRef.current) {
      throw new Error('Not authenticated')
    }

    const response = await authenticatedRequest<UserProfile>('/users/me/profile', tokenRef.current, {
      method: 'PUT',
      body: JSON.stringify(input),
    })

    // Update local state immediately for instant UI feedback
    setData((prev) =>
      prev
        ? {
            ...prev,
            profile: response,
          }
        : null
    )

    return response
  }, [])

  /**
   * Complete onboarding (sets onboarding_completed_at)
   */
  const completeOnboarding = useCallback(async (): Promise<UserProfile> => {
    if (!tokenRef.current) {
      throw new Error('Not authenticated')
    }

    const response = await authenticatedRequest<UserProfile>(
      '/users/me/profile/complete-onboarding',
      tokenRef.current,
      {
        method: 'POST',
      }
    )

    // Update local state immediately
    setData((prev) =>
      prev
        ? {
            ...prev,
            profile: response,
          }
        : null
    )

    return response
  }, [])

  /**
   * Complete product tour (sets product_tour_completed_at)
   */
  const completeProductTour = useCallback(async (): Promise<UserProfile> => {
    if (!tokenRef.current) {
      throw new Error('Not authenticated')
    }

    const response = await authenticatedRequest<UserProfile>(
      '/users/me/profile/complete-product-tour',
      tokenRef.current,
      {
        method: 'POST',
      }
    )

    // Update local state immediately
    setData((prev) =>
      prev
        ? {
            ...prev,
            profile: response,
          }
        : null
    )

    return response
  }, [])

  /**
   * Sign the investor agreement (creates audit trail, updates agreement_version)
   */
  const signAgreement = useCallback(async (fullLegalName: string, nameMismatchConfirmed = false): Promise<UserProfile> => {
    if (!tokenRef.current) {
      throw new Error('Not authenticated')
    }

    const sessionId = crypto.randomUUID()

    const response = await authenticatedRequest<UserProfile>(
      '/users/me/profile/sign-agreement',
      tokenRef.current,
      {
        method: 'POST',
        body: JSON.stringify({ full_legal_name: fullLegalName, session_id: sessionId, name_mismatch_confirmed: nameMismatchConfirmed }),
      }
    )

    // Update local state immediately
    setData((prev) =>
      prev
        ? {
            ...prev,
            profile: response,
          }
        : null
    )

    return response
  }, [])

  /**
   * Record an SMS opt-in or opt-out (writes a consent audit row)
   */
  const recordSmsConsent = useCallback(async (consented: boolean): Promise<UserProfile> => {
    if (!tokenRef.current) {
      throw new Error('Not authenticated')
    }

    const response = await authenticatedRequest<UserProfile>(
      '/users/me/profile/sms-consent',
      tokenRef.current,
      {
        method: 'POST',
        body: JSON.stringify({ consented, copy_version: SMS_CONSENT_COPY_VERSION } satisfies SmsConsentRequest),
      }
    )

    setData((prev) =>
      prev
        ? {
            ...prev,
            profile: response,
          }
        : null
    )

    return response
  }, [])

  /**
   * Reset product tour for replay (clears product_tour_completed_at)
   */
  const resetProductTour = useCallback(async (): Promise<UserProfile> => {
    if (!tokenRef.current) {
      throw new Error('Not authenticated')
    }

    const response = await authenticatedRequest<UserProfile>(
      '/users/me/profile/reset-product-tour',
      tokenRef.current,
      {
        method: 'POST',
      }
    )

    // Update local state immediately
    setData((prev) =>
      prev
        ? {
            ...prev,
            profile: response,
          }
        : null
    )

    return response
  }, [])

  /**
   * Accept the external-agent ToS (MRG-281).
   *
   * Mirrors `signAgreement` but hits the external-agent endpoint and updates
   * `external_agent_terms_accepted_at` / `external_agent_terms_version`.
   */
  const acceptExternalAgentTerms = useCallback(async (
    fullLegalName: string,
    nameMismatchConfirmed = false,
  ): Promise<UserProfile> => {
    if (!tokenRef.current) {
      throw new Error('Not authenticated')
    }

    const sessionId = crypto.randomUUID()

    const response = await authenticatedRequest<UserProfile>(
      '/users/me/profile/accept-external-agent-terms',
      tokenRef.current,
      {
        method: 'POST',
        body: JSON.stringify({
          full_legal_name: fullLegalName,
          session_id: sessionId,
          name_mismatch_confirmed: nameMismatchConfirmed,
        }),
      },
    )

    setData((prev) => (prev ? { ...prev, profile: response } : null))
    return response
  }, [])

  /**
   * Update per-module training progress (MRG-281).
   */
  const updateTrainingProgress = useCallback(async (
    moduleId: string,
    completed: boolean,
  ): Promise<UserProfile> => {
    if (!tokenRef.current) {
      throw new Error('Not authenticated')
    }

    const response = await authenticatedRequest<UserProfile>(
      '/users/me/profile/training-progress',
      tokenRef.current,
      {
        method: 'PUT',
        body: JSON.stringify({ module_id: moduleId, completed }),
      },
    )

    setData((prev) => (prev ? { ...prev, profile: response } : null))
    return response
  }, [])

  /**
   * Mark mandatory training as complete (MRG-281).
   */
  const completeTraining = useCallback(async (): Promise<UserProfile> => {
    if (!tokenRef.current) {
      throw new Error('Not authenticated')
    }

    const response = await authenticatedRequest<UserProfile>(
      '/users/me/profile/complete-training',
      tokenRef.current,
      { method: 'POST' },
    )

    setData((prev) => (prev ? { ...prev, profile: response } : null))
    return response
  }, [])

  /**
   * Reset training so the agent can replay (MRG-281).
   */
  const resetTraining = useCallback(async (): Promise<UserProfile> => {
    if (!tokenRef.current) {
      throw new Error('Not authenticated')
    }

    const response = await authenticatedRequest<UserProfile>(
      '/users/me/profile/reset-training',
      tokenRef.current,
      { method: 'POST' },
    )

    setData((prev) => (prev ? { ...prev, profile: response } : null))
    return response
  }, [])

  // Determine if user needs onboarding
  // Admins are exempt from onboarding
  const isAdmin = roles.includes('admin')
  const isInvestor = roles.includes('investor')
  const isExternalAgent = data?.profile?.agent_type === 'external'
  const needsOnboarding = !isAdmin && data !== null && data.profile.onboarding_completed_at === null

  // Determine if investor needs product tour
  // Only investors who have completed onboarding but not the tour
  const needsProductTour =
    isInvestor &&
    data !== null &&
    data.profile.onboarding_completed_at !== null &&
    data.profile.product_tour_completed_at === null

  // Determine if investor needs to sign the new agreement
  // Only applies to investors who completed onboarding but haven't signed the current version
  const needsNewAgreement =
    isInvestor &&
    data !== null &&
    data.profile.onboarding_completed_at !== null &&
    data.profile.agreement_version !== CURRENT_AGREEMENT_VERSION

  // External-agent ToS / training gates (MRG-281). Only meaningful when the
  // caller is an external agent — internal agents are auto-stamped at provision
  // time so these flags are always false for them, and investors / admins are
  // not parties to this Agreement.
  const needsExternalAgentTerms =
    isExternalAgent &&
    data !== null &&
    data.profile.external_agent_terms_version !== CURRENT_EXTERNAL_AGENT_TERMS_VERSION

  const needsTraining =
    isExternalAgent &&
    data !== null &&
    data.profile.training_completed_at === null

  const value: UserProfileContextValue = useMemo(
    () => ({
      data,
      isLoading,
      error,
      refetch,
      updateProfile,
      completeOnboarding,
      signAgreement,
      recordSmsConsent,
      acceptExternalAgentTerms,
      updateTrainingProgress,
      completeTraining,
      resetTraining,
      completeProductTour,
      resetProductTour,
      needsOnboarding,
      needsProductTour,
      needsNewAgreement,
      needsExternalAgentTerms,
      needsTraining,
    }),
    [
      data,
      isLoading,
      error,
      refetch,
      updateProfile,
      completeOnboarding,
      signAgreement,
      recordSmsConsent,
      acceptExternalAgentTerms,
      updateTrainingProgress,
      completeTraining,
      resetTraining,
      completeProductTour,
      resetProductTour,
      needsOnboarding,
      needsProductTour,
      needsNewAgreement,
      needsExternalAgentTerms,
      needsTraining,
    ],
  )

  return <UserProfileContext.Provider value={value}>{children}</UserProfileContext.Provider>
}

/**
 * Hook to access user profile context
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useUserProfileContext(): UserProfileContextValue {
  const context = useContext(UserProfileContext)
  if (!context) {
    throw new Error('useUserProfileContext must be used within a UserProfileProvider')
  }
  return context
}
